import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import WeddingTimelinePage from './pages/WeddingTimelinePage';
import WeddingInsightPage from './pages/WeddingInsightPage';
import WeddingInsightThankYou from './pages/WeddingInsightThankYou';
import WeddingInsightIntakePage from './pages/WeddingInsightIntakePage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import GuestWebsiteBuilder from './pages/GuestWebsiteBuilder';
import GuestWebsitePublic from './pages/GuestWebsitePublic';
import Timeline from './pages/Timeline';
import BudgetPlanner from './pages/BudgetPlanner';
import GuestList from './pages/GuestList';
import Vendors from './pages/Vendors';
import PaymentSuccess from './pages/PaymentSuccess';
import GuestRelationshipManager from './pages/GuestRelationshipManager';
import Checklist from './pages/Checklist';
import UpgradePage from './pages/UpgradePage';
import Details from './pages/Details';
import AccountPage from './pages/AccountPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminClientDetail from './pages/AdminClientDetail';
import AdminEmailComposer from './pages/AdminEmailComposer';
import AdminFeedback from './pages/AdminFeedback';
import AdminRevenue from './pages/AdminRevenue';
import AdminNewSignups from './pages/AdminNewSignups';
import AdminEmailAutomation from './pages/AdminEmailAutomation';
import AdminWeddingInsight from './pages/AdminWeddingInsight';
import AdminStripeEvents from './pages/AdminStripeEvents';
import AdminMonitoring from './pages/AdminMonitoring';
import SignupWithPayment from './pages/SignupWithPayment';
import PendingGuestEmails from './pages/PendingGuestEmails';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { Toaster } from 'sonner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('vowflow_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleAuth = (userData) => {
    setUser(userData);
    localStorage.setItem('vowflow_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('vowflow_user');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/wedding-timeline" element={<WeddingTimelinePage />} />
          <Route path="/wedding-insight" element={<WeddingInsightPage />} />
          <Route path="/wedding-insight/thank-you" element={<WeddingInsightThankYou />} />
          <Route path="/wedding-insight/intake" element={<WeddingInsightIntakePage />} />
          <Route path="/signup" element={<SignupWithPayment onAuth={handleAuth} />} />
          <Route 
            path="/auth" 
            element={
              user 
                ? (user.is_admin || user.role === 'admin' || user.role === 'super_admin' 
                    ? <Navigate to="/admin" /> 
                    : <Navigate to="/dashboard" />)
                : <AuthPage onAuth={handleAuth} />
            } 
          />
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route 
            path="/account" 
            element={user ? <AccountPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/guest-website" 
            element={user ? <GuestWebsiteBuilder user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/timeline" 
            element={user ? <Timeline user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/budget" 
            element={user ? <BudgetPlanner user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/guests" 
            element={user ? <GuestList user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/vendors" 
            element={user ? <Vendors user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/guest-manager" 
            element={user ? <GuestRelationshipManager user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/checklist" 
            element={user ? <Checklist user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/details" 
            element={user ? <Details user={user} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/pending-emails" 
            element={user ? <PendingGuestEmails user={user} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/upgrade" 
            element={user ? <UpgradePage user={user} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/payment-success" 
            element={user ? <PaymentSuccess user={user} /> : <Navigate to="/auth" />} 
          />
          {/* Admin Routes */}
          <Route 
            path="/admin/login" 
            element={<AdminLogin onLogin={handleAuth} />} 
          />
          <Route 
            path="/admin/magic-login" 
            element={<AdminLogin onLogin={handleAuth} />} 
          />
          <Route 
            path="/admin" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminDashboard user={user} onLogout={handleLogout} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/clients" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminDashboard user={user} onLogout={handleLogout} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/client/:clientId" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminClientDetail user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/email-clients" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminEmailComposer user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/feedback" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminFeedback user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/revenue" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminRevenue user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/new-signups" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminNewSignups user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/email-automation" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminEmailAutomation user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/wedding-insight" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminWeddingInsight user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/stripe-events" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminStripeEvents user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/monitoring" 
            element={user && (user.is_admin || user.role === 'admin' || user.role === 'super_admin') 
              ? <AdminMonitoring user={user} /> 
              : <Navigate to="/admin/login" />
            } 
          />
          <Route path="/wedding/:slug" element={<GuestWebsitePublic />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;